// The email template editor (spec C14 Templates; design/kit SettingsEditor): the wide slide-over (ed560)
// with To, CC, subject and body, a menu that inserts a value, the blocks to try in the preview, a
// live preview on a made-up record, Reset to default, and History. Only the Admin edits in Phase 1;
// the Manager sees it read only.
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  Api,
  EmailTemplateRow,
  SettingChange,
  TemplatePreview,
  TemplatesView,
} from '../auth/api';
import { ConfirmPopup } from '../shell/ConfirmPopup';
import { useModalLayer } from '../shell/modal-layer';
import { XIcon } from '../shell/icons';
import { stamp } from './code-rule-lines';
import { HistoryRows } from './HistoryRows';
import { fieldLabel, saveError, templateCopy as c } from './template-lines';

type Draft = { to: string; cc: string; subject: string; body: string };
const draftOf = (t: EmailTemplateRow): Draft => ({
  to: t.to,
  cc: t.cc,
  subject: t.subject,
  body: t.body,
});
const same = (a: Draft, b: Draft) =>
  a.to === b.to && a.cc === b.cc && a.subject === b.subject && a.body === b.body;

type Props = {
  api: Api;
  view: TemplatesView;
  template: EmailTemplateRow;
  onClose: () => void;
  onSaved: (toast: string) => void;
};

export function TemplateEditor({ api, view, template, onClose, onSaved }: Props) {
  const [orig, setOrig] = useState(() => draftOf(template));
  const [d, setD] = useState(orig);
  const [flags, setFlags] = useState<Record<string, boolean>>({
    mortgagee: true,
    not_escrowed: true,
    carrier_billed: true,
    carrier_contact: true,
    carrier_link: true,
    carrier_named: true,
  });
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'discard' | 'reset' | null>(null);
  const [history, setHistory] = useState<SettingChange[] | null>(null);
  const [histOpen, setHistOpen] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const layerRef = useRef<HTMLElement>(null);
  useModalLayer(layerRef);
  const dirty = !same(d, orig);
  const canEdit = view.canEdit;

  const requestClose = useCallback(() => {
    if (dirty) setConfirm('discard');
    else onClose();
  }, [dirty, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [requestClose]);

  useEffect(() => {
    let live = true;
    api.templateHistory(template.key).then((res) => {
      if (live && res.ok) setHistory(res.body.history);
    });
    return () => {
      live = false;
    };
  }, [api, template.key]);

  // The live preview, a moment after the last keystroke.
  useEffect(() => {
    let live = true;
    const timer = setTimeout(async () => {
      const res = await api.previewTemplate({ ...d, flags });
      if (live && res.ok) setPreview(res.body);
    }, 350);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [api, d, flags]);

  function insert(key: string) {
    const token = `{{${key}}}`;
    const el = bodyRef.current;
    const at = el ? el.selectionStart : d.body.length;
    const end = el ? el.selectionEnd : d.body.length;
    setD({ ...d, body: d.body.slice(0, at) + token + d.body.slice(end) });
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(at + token.length, at + token.length);
    });
  }

  async function save() {
    setBusy(true);
    setError(null);
    const res = await api.saveTemplate(template.key, d);
    setBusy(false);
    if (res.ok) {
      setOrig(draftOf(res.body.template));
      onSaved(c.saved(template.name));
    } else setError(saveError(res.error, res.body));
  }

  async function reset() {
    setConfirm(null);
    setBusy(true);
    const res = await api.resetTemplate(template.key);
    setBusy(false);
    if (res.ok) {
      const next = draftOf(res.body.template);
      setOrig(next);
      setD(next);
      onSaved(c.resetDone(template.name));
    } else setError(c.somethingWrong);
  }

  const field = (key: 'to' | 'cc' | 'subject', max: number) => (
    <label className="col g4">
      <span className="flab">{fieldLabel[key]}</span>
      <input
        className="input"
        value={d[key]}
        maxLength={max}
        disabled={!canEdit}
        onChange={(e) => setD({ ...d, [key]: e.target.value })}
      />
    </label>
  );

  return (
    <>
      <button
        type="button"
        className="scrim"
        aria-label={`Close ${template.name}`}
        onClick={requestClose}
      />
      <aside
        ref={layerRef}
        className="panel ed560"
        role="dialog"
        aria-modal="true"
        aria-labelledby="te-title"
      >
        <div className="p-head">
          <div className="col g4" style={{ minWidth: 0 }}>
            <h2 id="te-title" className="dtitle">
              {template.name}
            </h2>
            <span className="t12 muted ed-sub">{template.usedFor}</span>
          </div>
          <button type="button" className="xbtn" aria-label="Close" onClick={requestClose}>
            <XIcon />
          </button>
        </div>
        <div className="p-body">
          {!canEdit && <p className="fnote">{c.readOnly}</p>}
          <section className="esec" aria-label={c.email}>
            {field('to', view.limits.recipients)}
            {field('cc', view.limits.recipients)}
            {field('subject', view.limits.subject)}
            <label className="col g4">
              <span className="flab">{fieldLabel.body}</span>
              <textarea
                ref={bodyRef}
                className="input"
                rows={16}
                style={{ height: 'auto' }}
                value={d.body}
                maxLength={view.limits.body}
                disabled={!canEdit}
                onChange={(e) => setD({ ...d, body: e.target.value })}
              />
            </label>
            {canEdit && (
              <div role="group" aria-labelledby="te-ins">
                <div id="te-ins" className="flab">
                  {c.insert}
                </div>
                <div className="fopts">
                  {view.placeholders.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      className="chip chip-sm"
                      onClick={() => insert(p.key)}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <p className="fnote">{c.blocksNote}</p>
              </div>
            )}
          </section>
          <section className="esec" aria-labelledby="te-prev">
            <h3 id="te-prev" className="h3">
              {c.preview}
            </h3>
            <div className="fopts" role="group" aria-label={c.previewBlocks}>
              {view.flags.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={flags[f.key] ? 'chip chip-sm on' : 'chip chip-sm'}
                  aria-pressed={!!flags[f.key]}
                  onClick={() => setFlags({ ...flags, [f.key]: !flags[f.key] })}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {preview?.unknown.length ? (
              <p className="ferr" role="alert">
                {c.unknown(preview.unknown)}
              </p>
            ) : null}
            {preview && (
              <div className="okbox">
                <span className="t12 muted">
                  {c.previewTo(preview.preview.to, preview.preview.cc)}
                </span>
                <span className="t13 w6 ink">{preview.preview.subject}</span>
                <span className="t13 text2" style={{ whiteSpace: 'pre-wrap' }}>
                  {preview.preview.body}
                </span>
              </div>
            )}
          </section>
          <section className="esec" aria-label={c.history}>
            <button
              type="button"
              className="linkbtn"
              style={{ alignSelf: 'flex-start' }}
              aria-expanded={histOpen}
              onClick={() => setHistOpen(!histOpen)}
            >
              {histOpen ? c.hideHistory : history ? c.historyCount(history.length) : c.history}
            </button>
            {histOpen && history && (
              <HistoryRows
                rows={history.map((h) => ({
                  when: `${stamp(h.at)}, ${h.by ?? 'someone'}`,
                  field: fieldLabel[h.field as keyof typeof fieldLabel] ?? h.field,
                  before: short(h.before),
                  after: short(h.after),
                }))}
                empty={c.noHistory}
              />
            )}
          </section>
          {error && (
            <p className="ferr" role="alert">
              {error}
            </p>
          )}
        </div>
        <div className="p-foot">
          <button type="button" className="btn" onClick={requestClose}>
            {canEdit ? c.cancel : c.close}
          </button>
          {canEdit && !template.isDefault && (
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => setConfirm('reset')}
            >
              {c.reset}
            </button>
          )}
          <span className="grow"></span>
          {dirty && <span className="t12 amber w5">{c.unsaved}</span>}
          {canEdit && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!dirty || busy}
              onClick={() => void save()}
            >
              {c.save}
            </button>
          )}
        </div>
      </aside>
      {confirm === 'discard' && (
        <ConfirmPopup
          title={c.discardTitle(template.name)}
          line={c.discardLine}
          action={c.discard}
          cancel={c.keepEditing}
          danger
          onConfirm={onClose}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === 'reset' && (
        <ConfirmPopup
          title={c.resetTitle(template.name)}
          line={c.resetLine}
          action={c.reset}
          danger
          onConfirm={() => void reset()}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
}

/** A long body in the History list: the first 80 characters. */
const short = (v: unknown) => {
  const s = typeof v === 'string' ? v.replace(/\s+/g, ' ') : String(v ?? '');
  return s.length > 80 ? `${s.slice(0, 80)}...` : s;
};
